import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Modal from 'react-bootstrap/Modal';
import Icon from '@mui/material/Icon';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import SoftBox from 'components/SoftBox';
import SoftButton from 'components/SoftButton';
import SoftSelect from 'components/SoftSelect';
import Grid from '@mui/material/Grid';
import SoftDatePicker from 'components/SoftDatePicker';
import SoftTypography from 'components/SoftTypography';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Tooltip from '@mui/material/Tooltip';

function ModalDownloadData({ show, handleClose, onApplyFilters }) {
  // Estados para capturar los filtros
  const [reportType, setReportType] = useState('Detallado');
  const [transport, setTransport] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  // Estados para el toast de error
  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const transportOptions = [
    { value: 'aereo', label: 'Aéreo' },
    { value: 'maritimo', label: 'Marítimo' }
  ];

  // Formatea fecha a "YYYY-MM-DD"
  const formatDate = (date) => {
    if (!date) return null;
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    if (typeof date === 'string') {
      return date.split('T')[0];
    }
    const parsed = new Date(date);
    return !isNaN(parsed) ? parsed.toISOString().split('T')[0] : null;
  };

  // Cierra el toast de error
  const handleCloseError = (_, reason) => {
    if (reason === 'clickaway') return;
    setOpenError(false);
  };

  // Descarga el Excel según los filtros y el tipo de reporte
  const handleDownload = async () => {
    const fDesde = Array.isArray(dateFrom) ? dateFrom[0] : dateFrom;
    const fHasta = Array.isArray(dateTo)   ? dateTo[0]   : dateTo;
    const formattedFrom = formatDate(fDesde);
    const formattedTo   = formatDate(fHasta);

    if (!transport || !formattedFrom || !formattedTo) {
      setErrorMessage('Debe seleccionar transporte y ambas fechas.');
      setOpenError(true);
      return;
    }

    const transportMapping = { aereo: '1', maritimo: '2' };
    const transportParam   = transportMapping[transport];

    // Selecciona el endpoint según reportType
    const path = reportType === 'Compacto'
      ? '/api/finance/consolidated/download/compact'
      : '/api/finance/consolidated/download/detailed';

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}${path}`,
        {
          params: {
            tipoTransporte: transportParam,
            txtdesde:       formattedFrom,
            txthasta:       formattedTo
          },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: response.data.type });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;

      const suffix = reportType === 'Compacto' ? '_compacto' : '_detallado';
      const ext    = transportParam === '1' ? 'aerea' : 'maritima';
      link.setAttribute('download', `informacion_${ext}${suffix}.xlsx`);

      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error al descargar el archivo:", error);
      setErrorMessage('Ocurrió un error al descargar el archivo.');
      setOpenError(true);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        size="lg"
        aria-labelledby="Modal-Reporte-Consolidado"
        centered
        backdrop="static"
      >
        <Modal.Header closeButton>
          <Modal.Title>Reporte de Liquidaciones</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SoftBox mt={2}>
            <Grid container spacing={3}>
              {/* Tipo de reporte */}
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    aria-label="Tipo de reporte"
                    name="report_type"
                    value={reportType}
                    onChange={(e) => setReportType(e.target.value)}
                  >
                    <Tooltip title="Reporte resumido: solo consolidado sin desglose." arrow>
                      <FormControlLabel
                        value="Compacto"
                        control={<Radio />}
                        label="Compacto"
                      />
                    </Tooltip>
                    <Tooltip title="Reporte completo: incluye cada guía aérea y sus recargos." arrow>
                      <FormControlLabel
                        value="Detallado"
                        control={<Radio />}
                        label="Detallado"
                      />
                    </Tooltip>
                  </RadioGroup>
                </FormControl>
              </Grid>

              {/* Transporte */}
              <Grid item xs={12}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Transporte
                  </SoftTypography>
                  <SoftSelect
                    options={transportOptions}
                    placeholder="Selecciona el tipo de transporte..."
                    isClearable={false}
                    onChange={(opt) => setTransport(opt.value)}
                  />
                </SoftBox>
              </Grid>

              {/* Fecha Desde */}
              <Grid item xs={12} md={6}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Fecha Desde
                  </SoftTypography>
                  <SoftDatePicker
                    placeholder="Selecciona la fecha de inicio."
                    options={{ dateFormat: "Y-m-d", altInput: true, altFormat: "F j, Y" }}
                    onChange={(date) => setDateFrom(date)}
                  />
                </SoftBox>
              </Grid>

              {/* Fecha Hasta */}
              <Grid item xs={12} md={6}>
                <SoftBox display="flex" flexDirection="column" gap={1}>
                  <SoftTypography variant="caption" fontWeight="bold" color="text">
                    Fecha Hasta
                  </SoftTypography>
                  <SoftDatePicker
                    placeholder="Selecciona la fecha de término."
                    options={{ dateFormat: "Y-m-d", altInput: true, altFormat: "F j, Y" }}
                    onChange={(date) => setDateTo(date)}
                  />
                </SoftBox>
              </Grid>
            </Grid>
          </SoftBox>
        </Modal.Body>
        <Modal.Footer>
          <SoftButton variant="gradient" color="success" sx={{ mr: 2 }} onClick={handleDownload}>
            <Icon>download</Icon>&nbsp;Descargar
          </SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleClose}>
            Cancelar
          </SoftButton>
        </Modal.Footer>
      </Modal>

      {/* Toast de error */}
      <Snackbar
        open={openError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

ModalDownloadData.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired
};

export default ModalDownloadData;
